import { ageOn, CHILD_AGE_LIMIT, CHILD_REGISTER_LIMIT, cleanCenterName, newUserProfile, type SignupRole } from '@selfon/domain/browser';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { api, ApiError } from '../api';
import { DEV_USERS, useAuth } from '../auth/AuthContext';
import * as cognito from '../auth/cognito';
import type { SignInResult } from '../auth/cognito';
import { Alert, errorText, Field, ViewDialog } from '../components/ui';
import mark from '../assets/selfon-mark.png';
import wordmark from '../assets/selfon-wordmark.png';
import { config, configProblems } from '../config';
import { todayKst } from '../format';
import { ThemeSwitch } from '../theme';
import { Link } from 'react-router-dom';
import { Legal, type LegalKind } from './Legal';
import { BusinessInfo } from './PublicPages';

/*
 * 첫 화면: 이메일 로그인 · 회원가입(치료사/보호자/학생) · 아이디 찾기 · 비밀번호 찾기 · 약관.
 * 단일 HTML 데모는 브라우저 안의 로그인 서버(demo/identity)를, 실서비스는 Cognito 를 쓴다. 화면은 같다.
 */

type Screen = 'login' | 'role' | 'signup' | 'findId' | 'reset' | LegalKind;
const isDemo = config.authMode === 'demo';
/** 비밀번호 최소 길이: 실서비스는 Cognito 비밀번호 정책(10자, 소문자·숫자)과 같게 */
const PW_MIN = isDemo ? 8 : 10;
// 데모 빌드에서만 포함된다(다른 빌드에서는 이 import 가 빠진다)
const demoId = () => (__SELFON_DEMO__ ? import('../demo/identity') : Promise.reject(new Error('지금은 쓸 수 없는 기능입니다.')));

const ROLE_CARDS: Array<{ role: SignupRole; icon: string; title: string; desc: string }> = [
  { role: 'CENTER_DIRECTOR', icon: '🏫', title: '원장님', desc: '우리 센터를 열고 무료체험으로 시작합니다. 선생님들은 센터 가입 코드로 들어와요.' },
  { role: 'THERAPIST', icon: '🧑‍⚕️', title: '선생님', desc: '가입 코드로 소속 센터에 들어가거나 개인 선생님으로 활동합니다.' },
  { role: 'GUARDIAN', icon: '👪', title: '보호자', desc: `자녀(만 ${CHILD_REGISTER_LIMIT}세 미만)를 등록하고, 자녀 모드로 아이가 과제를 합니다.` },
  { role: 'STUDENT', icon: '🎒', title: '일반인', desc: `만 ${CHILD_AGE_LIMIT}세 이상 학생·성인이 직접 가입합니다. 더 어리면 보호자가 자녀로 등록해요.` },
];

/** 가입 첫 질문. 묶음을 고른 뒤 그 안의 역할을 한 번 더 묻는다. */
const ROLE_GROUPS: Array<{ id: string; icon: string; title: string; desc: string; ask: string; roles: readonly [SignupRole, SignupRole] }> = [
  { id: 'center', icon: '🏫', title: '원장님 · 선생님', desc: '센터를 운영하거나 아이들을 가르칩니다.', ask: '원장님이세요, 선생님이세요?', roles: ['CENTER_DIRECTOR', 'THERAPIST'] },
  { id: 'family', icon: '👪', title: '일반인 · 보호자', desc: '직접 배우거나 아이의 수업을 챙깁니다.', ask: '일반인이세요, 보호자세요?', roles: ['STUDENT', 'GUARDIAN'] },
];

/** 로그인 화면 왼쪽 소개(좁은 화면에서는 제목만) */
function WelcomeStory() {
  return (
    <aside className="welcome-story" aria-label="SelfOn 소개">
      <div className="story-wordmark"><img className="story-mark" src={mark} alt="" width={34} height={34} />SelfOn</div>
      <span className="eyebrow">CONNECTING EVERY STEP</span>
      <h2>함께하는 케어,<br />더 가벼운 하루.</h2>
      <p className="story-copy">센터의 일정부터 가정에서의 작은 실천까지. 아이의 성장을 위한 모든 순간을 하나로 이어갑니다.</p>
      <div className="story-preview" aria-hidden="true">
        <div className="preview-head"><strong>함께 만들어가는 하루</strong><span>MY CARE</span></div>
        {[['01', '일정 확인', '한눈에 보는 나의 케어 일정'], ['02', '치료와 기록', '차곡차곡 이어지는 성장 기록'], ['03', '가정에서의 실천', '오늘의 과제로 이어지는 변화']].map(([n, t, d]) => (
          <div key={n} className="preview-step"><span className="step-number">{n}</span><div><strong>{t}</strong><p>{d}</p></div><span className="step-dot" /></div>
        ))}
      </div>
      <p className="story-footer">작은 변화가 모여, 더 큰 성장으로.</p>
    </aside>
  );
}

export function Login() {
  const { notice } = useAuth();
  const [screen, setScreen] = useState<Screen>('login');
  const [role, setRole] = useState<SignupRole>('GUARDIAN');
  const [backFromForm, setBackFromForm] = useState(false); // 가입 양식에서 '이전'이면 두 번째 질문으로 돌아간다
  const [flash, setFlash] = useState<string | null>(null);
  const go = (s: Screen, msg?: string) => { setFlash(msg ?? null); setScreen(s); window.scrollTo(0, 0); };
  const problems = configProblems();
  const wide = screen === 'role' || screen === 'signup' || screen === 'terms' || screen === 'privacy';

  return (
    <div className="login-wrap">
      <WelcomeStory />
      <main className={`login-card${wide ? ' wide' : ''}`} id="main" tabIndex={-1}>
        <div className="login-top">
          <h1 className="login-brand"><img src={wordmark} alt="SelfOn" width={180} height={60} /></h1>
          <ThemeSwitch compact />
        </div>
        <p className="muted">센터와 가정을 잇는 케어 워크스페이스</p>
        {screen === 'login' && <div className="auth-heading"><h2>로그인</h2></div>}
        {notice && screen === 'login' && <Alert kind="info">{notice}</Alert>}
        {flash && <Alert kind="ok">{flash}</Alert>}
        {problems.length > 0 && <Alert>서비스 설정이 완료되지 않았습니다. 관리자에게 문의하세요.</Alert>}

        {config.authMode === 'dev' ? <DevLogin /> : <>
          {screen === 'login' && <LoginForm onGo={go} />}
          {screen === 'role' && <RolePick from={backFromForm ? role : undefined} onPick={(r) => { setRole(r); go('signup'); }} onBack={() => { setBackFromForm(false); go('login'); }} />}
          {screen === 'signup' && <SignupForm role={role} onBack={() => { setBackFromForm(true); go('role'); }} onCancel={() => { setBackFromForm(false); go('login'); }} />}
          {screen === 'findId' && <FindId onBack={() => go('login')} />}
          {screen === 'reset' && <ResetPassword onDone={() => go('login', '비밀번호를 바꿨습니다. 새 비밀번호로 로그인하세요.')} onBack={() => go('login')} />}
          {(screen === 'terms' || screen === 'privacy') && <Legal kind={screen} onBack={() => go('login')} />}
        </>}

        <footer className="login-foot">
          <button type="button" className="link" onClick={() => go('terms')}>이용약관</button>
          <span aria-hidden="true">·</span>
          <button type="button" className="link" onClick={() => go('privacy')}><strong>개인정보처리방침</strong></button>
          <span aria-hidden="true">·</span>
          <Link to="/refund">환불 정책</Link>
          <span aria-hidden="true">·</span>
          <Link to="/pricing">요금 안내</Link>
          <span aria-hidden="true">·</span>
          <Link to="/about">서비스 소개</Link>
          <BusinessInfo />
          <span className="login-copy">© 2026 SelfOn</span>
        </footer>
      </main>
    </div>
  );
}

function LoginForm({ onGo }: { onGo: (s: Screen) => void }) {
  const { signInDev, signInCognito, afterSignIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [secondary, setSecondary] = useState('');
  const [step, setStep] = useState<{ kind: 'password' } | Extract<SignInResult, { kind: 'newPassword' | 'totp' }>>({ kind: 'password' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handle = async (r: SignInResult) => { if (r.kind === 'ok') await afterSignIn(); else { setSecondary(''); setStep(r); } };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      if (isDemo) await signInDev(await (await demoId()).signIn(email, password));
      else if (step.kind === 'password') await handle(await signInCognito(email, password));
      else await handle(await step.complete(secondary));
    } catch (err) { setError(errorText(err)); } finally { setBusy(false); }
  };

  return (
    <>
      <form className="stack" onSubmit={submit}>
        {step.kind === 'password' && <>
          <Field id="email" label="이메일"><input id="email" type="email" autoComplete="username" required placeholder="name@example.com" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
          <Field id="password" label="비밀번호"><input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} /></Field>
        </>}
        {step.kind === 'newPassword' && (
          <Field id="newpw" label="새 비밀번호" hint="10자 이상, 영문 소문자·숫자 포함">
            <input id="newpw" type="password" autoComplete="new-password" minLength={10} required value={secondary} onChange={(e) => setSecondary(e.target.value)} aria-describedby="newpw-hint" />
          </Field>
        )}
        {step.kind === 'totp' && (
          <Field id="totp" label="인증 앱 6자리 코드">
            <input id="totp" inputMode="numeric" autoComplete="one-time-code" pattern="\d{6}" maxLength={6} required value={secondary} onChange={(e) => setSecondary(e.target.value)} />
          </Field>
        )}
        {error && <Alert>{error}</Alert>}
        <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={busy}>
          {busy ? '확인 중…' : step.kind === 'password' ? '로그인' : step.kind === 'newPassword' ? '비밀번호 정하고 로그인' : '확인'}
        </button>
      </form>
      <div className="login-links">
        <button type="button" className="link" onClick={() => onGo('findId')}>아이디 찾기</button>
        <span aria-hidden="true">·</span>
        <button type="button" className="link" onClick={() => onGo('reset')}>비밀번호 찾기</button>
      </div>
      <div className="login-signup">
        <span className="muted">아직 계정이 없으신가요?</span>
        <button type="button" className="btn btn-outline btn-block" onClick={() => onGo('role')}>회원가입</button>
      </div>
      <p className="hint center">30분 동안 사용하지 않으면 자동 로그아웃됩니다.</p>
    </>
  );
}

function Steps({ current }: { current: 1 | 2 | 3 }) {
  return (
    <ol className="steps" aria-label="가입 단계">
      {['역할 선택', '정보 입력', '시작'].map((l, i) => <li key={l} className={i + 1 === current ? 'on' : i + 1 < current ? 'done' : ''} aria-current={i + 1 === current ? 'step' : undefined}><span className="step-no" aria-hidden="true">{i + 1}</span>{l}</li>)}
    </ol>
  );
}

function RolePick({ from, onPick, onBack }: { from?: SignupRole; onPick: (r: SignupRole) => void; onBack: () => void }) {
  const [group, setGroup] = useState(() => ROLE_GROUPS.find((g) => from && g.roles.includes(from)) ?? null);
  // 질문이 바뀌면 누른 버튼이 사라지므로 키보드·화면 낭독기 사용자를 새 질문으로 옮긴다
  const title = useRef<HTMLHeadingElement>(null);
  const asked = useRef(false);
  useEffect(() => { if (asked.current) title.current?.focus(); asked.current = true; }, [group]);
  const card = (key: string, c: { icon: string; title: string; desc: string }, onClick: () => void) => (
    <button key={key} type="button" className="role-card" onClick={onClick}>
      <span className="role-icon" aria-hidden="true">{c.icon}</span>
      <strong>{c.title}</strong>
      <span className="muted small">{c.desc}</span>
    </button>
  );
  return (
    <div className="stack">
      <Steps current={1} />
      <h2 ref={title} tabIndex={-1}>{group ? group.ask : '어떤 분이세요?'}</h2>
      <div className="role-cards">
        {group
          ? group.roles.map((r) => card(r, ROLE_CARDS.find((c) => c.role === r)!, () => onPick(r)))
          : ROLE_GROUPS.map((g) => card(g.id, g, () => setGroup(g)))}
      </div>
      {group
        ? <button type="button" className="btn btn-ghost" onClick={() => setGroup(null)}>이전</button>
        : <button type="button" className="btn btn-ghost" onClick={onBack}>로그인으로 돌아가기</button>}
    </div>
  );
}

function SignupForm({ role, onBack, onCancel }: { role: SignupRole; onBack: () => void; onCancel: () => void }) {
  // 약관은 화면을 바꾸지 않고 창으로 연다(바꾸면 입력한 내용이 사라진다)
  const [legal, setLegal] = useState<LegalKind | null>(null);
  const { signInDev, setDevUser, signInCognito, afterSignIn } = useAuth();
  const [f, setF] = useState({ name: '', email: '', password: '', password2: '', phone: '', birthDate: '', centerCode: '', centerName: '', independent: false, terms: false, privacy: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 실서비스(Cognito): 정보 입력 → 이메일 인증 코드 → (자동 로그인 후) 가입 정보 저장. 저장이 실패하면 finish 에서 고쳐 다시 시도
  const [stage, setStage] = useState<'form' | 'code' | 'finish'>('form');
  const [code, setCode] = useState('');
  const [info, setInfo] = useState<string | null>(null);
  const card = ROLE_CARDS.find((c) => c.role === role)!;
  const today = todayKst();
  const underAge = role === 'STUDENT' && /^\d{4}-\d{2}-\d{2}$/.test(f.birthDate) && ageOn(f.birthDate, today) < CHILD_AGE_LIMIT;
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  const onboardBody = () => ({
    name: f.name, phone: f.phone, birthDate: role === 'STUDENT' ? f.birthDate : undefined,
    centerCode: role === 'THERAPIST' && !f.independent ? f.centerCode : undefined, independent: role === 'THERAPIST' && f.independent ? true : undefined, centerName: role === 'CENTER_DIRECTOR' ? f.centerName : undefined,
  });
  // 서버 규칙과 같은 검사를 먼저 해서 계정만 만들어지고 끝나는 일을 줄인다
  const precheck = () => {
    newUserProfile({
      userId: 'check', role, name: f.name, phone: f.phone, birthDate: f.birthDate || undefined, now: new Date().toISOString(), todayKst: today,
      independent: role === 'THERAPIST' && f.independent, centerId: role === 'THERAPIST' ? f.centerCode || undefined : role === 'CENTER_DIRECTOR' ? cleanCenterName(f.centerName) : undefined,
    });
    if (role === 'THERAPIST' && !f.independent && !f.centerCode.trim()) throw new Error('센터 가입 코드를 입력하세요.');
  };
  const finishCognito = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null); setInfo(null);
    try {
      if (stage === 'code') { await cognito.confirmSignUp(f.email, code); setStage('finish'); }
      const r = await signInCognito(f.email, f.password);
      if (r.kind !== 'ok') throw new Error('가입은 끝났습니다. 로그인 화면에서 다시 로그인해 주세요.');
      try { await api.onboard(onboardBody()); }
      catch (err) { if (!(err instanceof ApiError && err.status === 409)) throw err; } // 이미 저장됨(다시 시도한 경우)
      await afterSignIn();
    } catch (err) { setError(errorText(err)); setBusy(false); }
  };

  if (stage !== 'form') {
    return (
      <form className="stack" onSubmit={finishCognito}>
        <Steps current={3} />
        <h2>{stage === 'code' ? '이메일 인증' : '가입 마무리'}</h2>
        {stage === 'code' ? (
          <>
            <p className="muted"><strong>{f.email}</strong>(으)로 보낸 6자리 인증 코드를 입력하세요. 메일이 보이지 않으면 스팸함도 확인해 주세요.</p>
            <Field id="su-otp" label="인증 코드">
              <input id="su-otp" required inputMode="numeric" autoComplete="one-time-code" maxLength={6} pattern="\d{6}" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
            </Field>
            <button type="button" className="link" disabled={busy} onClick={async () => {
              setError(null); setInfo(null);
              try { await cognito.resendSignUpCode(f.email); setInfo('인증 코드를 다시 보냈습니다.'); } catch (err) { setError(errorText(err)); }
            }}>코드 다시 받기</button>
          </>
        ) : (
          <>
            <p className="muted">이메일 인증은 끝났습니다. 가입 정보를 저장하지 못했어요. 내용을 확인하고 다시 시도해 주세요.</p>
            {role === 'THERAPIST' && !f.independent && (
              <Field id="su-code2" label="센터 가입 코드"><input id="su-code2" required maxLength={20} autoCapitalize="characters" value={f.centerCode} onChange={set('centerCode')} /></Field>
            )}
            {role === 'CENTER_DIRECTOR' && (
              <Field id="su-center2" label="센터 이름"><input id="su-center2" required maxLength={40} value={f.centerName} onChange={set('centerName')} /></Field>
            )}
          </>
        )}
        {info && <Alert kind="ok">{info}</Alert>}
        {error && <Alert>{error}</Alert>}
        <button type="submit" className="btn btn-primary btn-block" disabled={busy}>{busy ? '확인 중…' : stage === 'code' ? '인증하고 시작하기' : '다시 시도'}</button>
        <button type="button" className="btn btn-ghost" onClick={onCancel}>로그인으로 돌아가기</button>
      </form>
    );
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (f.password !== f.password2) { setError('비밀번호 확인이 일치하지 않습니다.'); return; }
    if (!f.terms || !f.privacy) { setError('필수 약관에 동의해 주세요.'); return; }
    if (!isDemo) {
      setBusy(true);
      try {
        precheck();
        await cognito.signUp({ email: f.email, password: f.password, name: f.name, role, birthDate: role === 'STUDENT' ? f.birthDate : undefined });
        setStage('code');
      } catch (err) { setError(errorText(err)); }
      setBusy(false);
      return;
    }
    setBusy(true);
    let sub: string | null = null;
    const id = await demoId();
    try {
      precheck();
      id.validatePassword(f.password);
      sub = await id.signUp({ email: f.email, password: f.password, role, name: f.name, phone: f.phone });
      setDevUser(sub);
      const r = await api.onboard(onboardBody());
      if (r.centerId) await id.setCenter(sub, r.centerId);
      await signInDev(sub);
    } catch (err) {
      // 가입 마무리(센터 코드 등)에 실패하면 방금 만든 계정을 지운다
      if (sub) { await id.removeAccount(sub); setDevUser(null); }
      setError(errorText(err));
      setBusy(false);
    }
  };

  return (<>
    <ViewDialog title={legal === 'privacy' ? '개인정보처리방침' : '이용약관'} open={Boolean(legal)} onClose={() => setLegal(null)} wide>
      {/* 약관 안의 링크는 새 탭으로 연다. 같은 탭으로 가면 입력한 가입 내용이 사라진다 */}
      <div onClickCapture={(e) => {
        const a = (e.target as HTMLElement).closest('a');
        if (!a || a.href.startsWith('mailto:')) return;
        e.preventDefault();
        e.stopPropagation();
        window.open(a.href, '_blank', 'noopener');
      }}>
        {legal && <Legal kind={legal} />}
      </div>
    </ViewDialog>
    <form className="stack" onSubmit={submit}>
      <Steps current={2} />
      <h2>{card.icon} {card.title} 가입</h2>
      <div className="form-grid">
        <Field id="su-name" label="이름"><input id="su-name" required maxLength={20} autoComplete="name" value={f.name} onChange={set('name')} /></Field>
        <Field id="su-phone" label="휴대폰 번호"><input id="su-phone" required inputMode="tel" autoComplete="tel" placeholder="010-0000-0000" value={f.phone} onChange={set('phone')} /></Field>
        <Field id="su-email" label="이메일(아이디)"><input id="su-email" type="email" required autoComplete="email" value={f.email} onChange={set('email')} /></Field>
        {role === 'STUDENT' && <Field id="su-birth" label="생년월일"><input id="su-birth" type="date" required max={today} value={f.birthDate} onChange={set('birthDate')} /></Field>}
        {role === 'CENTER_DIRECTOR' && (
          <Field id="su-center" label="센터 이름">
            <input id="su-center" required maxLength={40} placeholder="예: 햇살아동발달센터" value={f.centerName} onChange={set('centerName')} aria-describedby="su-center-hint" />
          </Field>
        )}
        {role === 'THERAPIST' && (
          <label className="teacher-consent"><input type="checkbox" checked={f.independent} onChange={(e) => setF({ ...f, independent: e.target.checked })} /> 소속 센터 없이 개인 선생님으로 활동합니다</label>
        )}
        {role === 'THERAPIST' && !f.independent && (
          <Field id="su-code" label="센터 가입 코드" hint="소속 센터의 원장님께 받은 코드입니다.">
            <input id="su-code" required maxLength={20} autoCapitalize="characters" value={f.centerCode} onChange={set('centerCode')} aria-describedby="su-code-hint" />
          </Field>
        )}
        <Field id="su-pw" label="비밀번호" hint={isDemo ? '8자 이상, 영문과 숫자를 모두 넣어 주세요.' : '10자 이상, 영문 소문자와 숫자를 모두 넣어 주세요.'}><input id="su-pw" type="password" required minLength={PW_MIN} autoComplete="new-password" value={f.password} onChange={set('password')} aria-describedby="su-pw-hint" /></Field>
        <Field id="su-pw2" label="비밀번호 확인"><input id="su-pw2" type="password" required minLength={PW_MIN} autoComplete="new-password" value={f.password2} onChange={set('password2')} /></Field>
      </div>
      {underAge && <Alert>만 {CHILD_AGE_LIMIT}세 미만은 직접 가입할 수 없어요. 보호자가 가입한 뒤 '자녀'로 등록하면 자녀 모드에서 학생 화면을 쓸 수 있습니다.</Alert>}
      <fieldset className="consents">
        <legend className="sr-only">약관 동의</legend>
        <label><input type="checkbox" checked={f.terms && f.privacy} onChange={(e) => setF({ ...f, terms: e.target.checked, privacy: e.target.checked })} /> <strong>모두 동의합니다</strong></label>
        <label><input type="checkbox" checked={f.terms} onChange={(e) => setF({ ...f, terms: e.target.checked })} /> [필수] <button type="button" className="link" onClick={() => setLegal('terms')}>이용약관</button> 동의</label>
        <label><input type="checkbox" checked={f.privacy} onChange={(e) => setF({ ...f, privacy: e.target.checked })} /> [필수] <button type="button" className="link" onClick={() => setLegal('privacy')}>개인정보 수집·이용</button> 동의 (이름·이메일·휴대폰{role === 'STUDENT' ? '·생년월일' : ''})</label>
        <p className="hint consent-note">목적: 회원 식별·로그인·서비스 제공 · 보유: 탈퇴 시까지(법령상 보관 기록 제외) · 동의하지 않을 수 있으나 그러면 가입할 수 없습니다.</p>
      </fieldset>
      {error && <Alert>{error}</Alert>}
      <div className="btn-row">
        <button type="button" className="btn btn-ghost" onClick={onBack}>이전</button>
        <button type="submit" className="btn btn-primary" disabled={busy || underAge}>{busy ? '가입 중…' : '가입하고 시작하기'}</button>
      </div>
    </form>
  </>);
}

function FindId({ onBack }: { onBack: () => void }) {
  const [f, setF] = useState({ name: '', phone: '' });
  const [result, setResult] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (!isDemo) {
    return <Step title="아이디 찾기" onBack={onBack}><Alert kind="info">아이디는 가입할 때 쓴 이메일입니다. 기억나지 않으면 소속 센터나 <Link to="/support">고객센터</Link>에 문의하세요.</Alert></Step>;
  }
  return (
    <Step title="아이디 찾기" onBack={onBack}>
      <p className="muted">가입할 때 입력한 이름과 휴대폰 번호로 확인합니다.</p>
      <form className="stack" onSubmit={async (e) => {
        e.preventDefault(); setError(null); setResult(null);
        try { setResult(await (await demoId()).findEmails(f.name, f.phone)); } catch (err) { setError(errorText(err)); }
      }}>
        <Field id="fi-name" label="이름"><input id="fi-name" required autoComplete="name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <Field id="fi-phone" label="휴대폰 번호"><input id="fi-phone" required inputMode="tel" autoComplete="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></Field>
        {error && <Alert>{error}</Alert>}
        {result && (result.length
          ? <Alert kind="ok">가입한 이메일: <strong className="mono">{result.join(', ')}</strong> (일부를 가렸습니다)</Alert>
          : <Alert kind="info">일치하는 계정이 없습니다. 이름과 번호를 다시 확인하세요.</Alert>)}
        <button type="submit" className="btn btn-primary btn-block">아이디 찾기</button>
      </form>
    </Step>
  );
}

function ResetPassword({ onDone, onBack }: { onDone: () => void; onBack: () => void }) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [f, setF] = useState({ code: '', pw: '', pw2: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (fn: () => Promise<void>) => { setBusy(true); setError(null); try { await fn(); } catch (e) { setError(errorText(e)); } finally { setBusy(false); } };

  return (
    <Step title="비밀번호 찾기" onBack={onBack}>
      {!sent ? (
        <form className="stack" onSubmit={(e) => { e.preventDefault(); void run(async () => {
          if (isDemo) {
            // 데모는 메일을 보낼 수 없어 코드를 개발자 도구 콘솔에만 적는다(화면에는 실서비스와 같은 안내)
            const code = await (await demoId()).requestReset(email);
            if (code) console.info(`[SelfOn 데모] ${email} 비밀번호 재설정 코드: ${code}`);
          } else await cognito.forgotPassword(email);
          setSent(true);
        }); }}>
          <p className="muted">가입한 이메일로 인증 코드를 보내 드립니다.</p>
          <Field id="rp-email" label="이메일"><input id="rp-email" type="email" required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
          {error && <Alert>{error}</Alert>}
          <button type="submit" className="btn btn-primary btn-block" disabled={busy}>인증 코드 받기</button>
        </form>
      ) : (
        <form className="stack" onSubmit={(e) => { e.preventDefault(); void run(async () => {
          if (f.pw !== f.pw2) throw new Error('새 비밀번호 확인이 일치하지 않습니다.');
          if (isDemo) await (await demoId()).confirmReset(email, f.code, f.pw);
          else await cognito.confirmPassword(email, f.code, f.pw);
          onDone();
        }); }}>
          <Alert kind="info">{email}(으)로 가입된 계정이 있으면 인증 코드를 보냈습니다. 10분 안에 입력하세요.</Alert>
          <Field id="rp-code" label="인증 코드 6자리"><input id="rp-code" required inputMode="numeric" pattern="\d{6}" maxLength={6} autoComplete="one-time-code" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} /></Field>
          <Field id="rp-pw" label="새 비밀번호" hint={isDemo ? '8자 이상, 영문과 숫자 포함' : '10자 이상, 영문 소문자와 숫자 포함'}><input id="rp-pw" type="password" required minLength={8} autoComplete="new-password" value={f.pw} onChange={(e) => setF({ ...f, pw: e.target.value })} aria-describedby="rp-pw-hint" /></Field>
          <Field id="rp-pw2" label="새 비밀번호 확인"><input id="rp-pw2" type="password" required minLength={8} autoComplete="new-password" value={f.pw2} onChange={(e) => setF({ ...f, pw2: e.target.value })} /></Field>
          {error && <Alert>{error}</Alert>}
          <button type="submit" className="btn btn-primary btn-block" disabled={busy}>비밀번호 바꾸기</button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setSent(false); setF({ code: '', pw: '', pw2: '' }); }}>코드 다시 받기</button>
        </form>
      )}
    </Step>
  );
}

function Step({ title, onBack, children }: { title: string; onBack: () => void; children: ReactNode }) {
  return (
    <div className="stack">
      <h2>{title}</h2>
      {children}
      <button type="button" className="btn btn-ghost" onClick={onBack}>로그인으로 돌아가기</button>
    </div>
  );
}

/** 로컬 개발(메모리 API): 시드 사용자로 바로 들어간다 */
function DevLogin() {
  const { signInDev } = useAuth();
  return (
    <div className="stack">
      <Alert kind="info">로컬 개발 모드입니다. 메모리 API(가상 데이터)의 사용자로 들어갑니다.</Alert>
      {DEV_USERS.map((u) => <button key={u.id} type="button" className="btn btn-outline btn-block" onClick={() => signInDev(u.id)}>{u.label}로 들어가기</button>)}
    </div>
  );
}
